import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import TuneIcon from "@mui/icons-material/Tune";
import DeleteOutlineOutlined from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ArticleIcon from "@mui/icons-material/Article";
import extractExcerpt from "./projects-card-excerpt";
import * as React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Avatar,
  Card,
  CardContent,
  Chip,
  Stack,
  Grid,
  Typography,
  Box,
  Link,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
  useTheme,
  alpha
} from "@mui/material";
import { useDialog } from "../../hooks/use-dialog";
import { DeleteProjectDialog } from "./delete-project-dialog";
import defaultFeaturedImage from "./default-featured-image.webp";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../core/hooks/useStore";

type ProjectsCardProps = {
  id: string;
  name: string;
  key?: number;
  status?: string;
  content?: string | null;
  image?: string | null;
};

type StatusData = {
  text: string;
  generating?: boolean;
  completed?: boolean;
};

const STATUS_TEXT: { [key: string]: StatusData } = {
  created: {
    text: "Created Project",
  },
  added_sources: {
    text: "Added Sources",
  },
  updated_brand_voice: {
    // TODO: rename this status in DB to selected_integration
    text: "Updated Integration",
  },
  selected_integration: {
    text: "Selected Integration",
  },
  creating_outline: {
    text: "Generating Outline",
    generating: true,
  },
  created_outline: {
    text: "Created Outline",
  },
  creating_article: {
    text: "Creating Article",
    generating: true,
  },
  created_article: {
    text: "Created Article",
    completed: true,
  },
  published: {
    text: "Published",
    completed: true,
  },
};

const statusToChip = (status: string) => {
  const statusData = STATUS_TEXT[status];
  if (!statusData) {
    return <></>;
  }
  const { text, generating, completed } = statusData;
  const color = completed ? "success" : generating ? "primary" : "default";
  const icon = completed ? (
    <CheckCircleIcon sx={{ fontSize: 16 }} />
  ) : generating ? (
    <CircularProgress size={14} thickness={6} />
  ) : (
    <TuneIcon sx={{ fontSize: 16 }} />
  );
  return (
    <Chip 
      icon={icon} 
      label={text} 
      color={color} 
      size="small"
      sx={{ fontWeight: 500 }}
    />
  );
};

const MoreMenu = (props: ProjectsCardProps) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const deleteProjectDialog = useDialog();
  const handleDelete = () => {
    setAnchorEl(null);
    deleteProjectDialog.handleOpen();
  };
  return (
    <div>
      <DeleteProjectDialog
        onClose={deleteProjectDialog.handleClose}
        open={deleteProjectDialog.open}
        projectName={props.name}
        projectID={props.id}
      />
      <IconButton 
        aria-label="more" 
        aria-haspopup="true" 
        onClick={handleClick}
        size="small"
        sx={{ 
          '&:hover': { 
            bgcolor: 'action.hover' 
          } 
        }}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuItem key="delete" onClick={handleDelete}>
          <DeleteOutlineOutlined color="error" fontSize="small" sx={{ mr: 1 }} /> 
          Delete Project
        </MenuItem>
      </Menu>
    </div>
  );
};

const ProjectsCard = (props: ProjectsCardProps) => {
  const theme = useTheme();
  const image = props.image ? props.image : defaultFeaturedImage;
  const status = props.status ? props.status : "created";
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const getStatusColor = () => {
    const statusData = STATUS_TEXT[status];
    if (statusData?.completed) return theme.palette.success.main;
    if (statusData?.generating) return theme.palette.info.main;
    return theme.palette.grey[400];
  };

  return (
    <Grid item xs={12}>
      <Card 
        sx={{ 
          transition: 'all 0.3s',
          border: `1px solid ${alpha(getStatusColor(), 0.2)}`,
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: theme.shadows[6],
            borderColor: getStatusColor()
          }
        }}
      >
        <CardContent>
          <Stack direction="row" spacing={3}>
            {/* Featured Image */}
            <Link 
              component={RouterLink} 
              to={`/editor/${props.id}`}
              sx={{ 
                flexShrink: 0,
                transition: 'transform 0.3s',
                '&:hover': {
                  transform: 'scale(1.05)'
                }
              }}
            >
              <Avatar
                src={image}
                alt={props.name}
                sx={{
                  width: { xs: 100, md: 140 },
                  height: { xs: 100, md: 140 },
                  borderRadius: 2,
                  boxShadow: theme.shadows[3],
                  border: `2px solid ${theme.palette.background.paper}`
                }}
                variant="rounded"
              >
                <ArticleIcon sx={{ fontSize: 40, color: theme.palette.grey[400] }} />
              </Avatar>
            </Link>

            {/* Content Section */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack spacing={1}>
                {/* Title and Status Row */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1 }}>
                  <Link
                    component={RouterLink}
                    to={`/editor/${props.id}`}
                    underline="none"
                    sx={{ 
                      flex: 1,
                      '&:hover .title-text': { 
                        color: theme.palette.primary.main,
                        textDecoration: 'underline' 
                      } 
                    }}
                  >
                    <Typography
                      variant="h5"
                      className="title-text"
                      sx={{
                        fontWeight: 600,
                        color: 'text.primary',
                        display: '-webkit-box',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: 2,
                        transition: 'color 0.2s',
                        lineHeight: 1.3
                      }}
                    >
                      {props.name}
                    </Typography>
                  </Link>
                  
                  <Box sx={{ flexShrink: 0, alignSelf: 'center' }}>
                    {statusToChip(status)}
                  </Box>
                </Box>

                {/* Content Excerpt */}
                <Box 
                  sx={{ 
                    p: 2, 
                    bgcolor: alpha(theme.palette.grey[100], 0.5),
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`
                  }}
                >
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{
                      display: '-webkit-box',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      WebkitBoxOrient: 'vertical',
                      WebkitLineClamp: 3,
                      lineHeight: 1.6,
                      minHeight: '3.6em'
                    }}
                  >
                    {(props.content?.trim() && extractExcerpt(300, props.content)) || (
                      <Box component="span" sx={{ fontStyle: 'italic', color: theme.palette.text.disabled }}>
                        No content yet. Click to start editing this project.
                      </Box>
                    )}
                  </Typography>
                </Box>

              </Stack>
            </Box>

            {/* More Menu */}
            <Box sx={{ flexShrink: 0 }}>
              <MoreMenu {...props} />
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Grid>
  );
};

export default ProjectsCard;